import { useState, useEffect } from "react";
import { api } from "../api";
import { formatPrice } from "../data/currency";
import { TableRowSkeleton } from "../components/Skeleton";

function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [games, setGames] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [sales, setSales] = useState([]);

  const [showModal, setShowModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [modalError, setModalError] = useState("");
  const [tabLoading, setTabLoading] = useState(false);
  const [viewGame, setViewGame] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(setOverview).catch(() => {});
  }, []);

  const fetchTabData = async (tab) => {
    setTabLoading(true);
    try {
      if (tab === "games") {
        const data = await api.get("/admin/games");
        setGames(data.data || data);
      } else if (tab === "users") {
        const data = await api.get("/admin/users");
        setUsers(data.data || data);
      } else if (tab === "categories") {
        const data = await api.get("/admin/categories");
        setCategories(data);
      } else if (tab === "developers") {
        const data = await api.get("/admin/developers");
        setDevelopers(data.data || data);
      } else if (tab === "sales") {
        const data = await api.get("/admin/sales");
        setSales(data.data || data);
      }
    } catch (e) {
      // ignore
    }
    setTabLoading(false);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab !== "overview") fetchTabData(tab);
  };

  const openModal = (type, item = null) => {
    setEditItem(item);
    setShowModal(type);
    setModalError("");
  };

  const closeModal = () => {
    setShowModal(null);
    setEditItem(null);
    setModalError("");
    setScreenshots([]);
  };

  const openViewGame = (game) => {
    setViewGame(game);
  };

  const closeViewGame = () => {
    setViewGame(null);
  };

  const handleDeleteGame = async (gameId) => {
    await api.delete(`/admin/games/${gameId}`);
    setGames((prev) => prev.filter((g) => g.id !== gameId));
  };

  const handleUpdateStatus = async (gameId, status) => {
    await api.put(`/admin/games/${gameId}/status`, { status });
    setGames((prev) => prev.map((g) => (g.id === gameId ? { ...g, status } : g)));
  };

  const handleDeleteUser = async (userId) => {
    await api.delete(`/admin/users/${userId}`);
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleDeleteCategory = async (catId) => {
    await api.delete(`/admin/categories/${catId}`);
    setCategories((prev) => prev.filter((c) => c.id !== catId));
  };

  const handleDeleteDev = async (devId) => {
    await api.delete(`/admin/developers/${devId}`);
    setDevelopers((prev) => prev.filter((d) => d.id !== devId));
  };

  const handleSaveDeveloper = async (e) => {
    e.preventDefault();
    setModalError("");
    const form = e.target;
    const body = {
      username: form.username.value,
      email: form.email.value,
      phone: form.phone.value,
      password: form.password.value,
      bio: form.bio.value || null,
    };
    try {
      if (editItem) {
        const data = await api.put(`/admin/developers/${editItem.id}`, body);
        setDevelopers((prev) => prev.map((d) => (d.id === editItem.id ? data : d)));
      } else {
        const data = await api.post("/admin/developers", body);
        setDevelopers((prev) => [data, ...prev]);
      }
      closeModal();
    } catch (err) {
      setModalError(err.message || "Save failed");
    }
  };

  const [screenshots, setScreenshots] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const handleScreenshots = (e) => {
    const files = Array.from(e.target.files);
      const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      setModalError(`Some images exceed 10MB limit: ${oversized.map((f) => f.name).join(", ")}`);
      e.target.value = "";
      return;
    }
    const remaining = 10 - screenshots.length;
    setScreenshots((prev) => [...prev, ...files.slice(0, remaining)].slice(0, 10));
    e.target.value = "";
  };

  const removeScreenshot = (index) => {
    setScreenshots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveGame = async (e) => {
    e.preventDefault();
    setModalError("");
    setUploadProgress(0);
    setUploading(true);
    const form = e.target;
    const fd = new FormData();
    fd.append("title", form.title.value);
    fd.append("category_id", form.category_id.value);
    fd.append("price", form.price.value);
    fd.append("description", form.description.value || "");
    fd.append("trailer_url", form.trailer_url.value || "");
    fd.append("version", form.version.value);
    if (form.changelog.value) fd.append("changelog", form.changelog.value);
    if (form.download_link.value) fd.append("download_link", form.download_link.value);
    if (form.cover.files[0]) {
      if (form.cover.files[0].size > MAX_FILE_SIZE) {
        setModalError("Cover image exceeds 10MB limit");
        setUploading(false);
        return;
      }
      fd.append("cover", form.cover.files[0]);
    }
    if (form.thumbnail.files[0]) {
      if (form.thumbnail.files[0].size > MAX_FILE_SIZE) {
        setModalError("Thumbnail exceeds 10MB limit");
        setUploading(false);
        return;
      }
      fd.append("thumbnail", form.thumbnail.files[0]);
    }
    screenshots.forEach((file) => fd.append("screenshots[]", file));

    try {
      if (editItem) {
        fd.append("_method", "PUT");
        const data = await api.upload(`/admin/games/${editItem.id}`, fd, setUploadProgress);
        setGames((prev) => prev.map((g) => (g.id === editItem.id ? data : g)));
      } else {
        const data = await api.upload("/admin/games", fd, setUploadProgress);
        setGames((prev) => [data, ...prev]);
      }
      setScreenshots([]);
      closeModal();
    } catch (err) {
      setModalError(err.message || "Save failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    setModalError("");
    const form = e.target;
    const body = {
      username: form.username.value,
      phone: form.phone.value,
      role: form.role.value,
      bio: form.bio.value || null,
    };
    if (form.password.value) body.password = form.password.value;

    try {
      if (editItem) {
        const data = await api.put(`/admin/users/${editItem.id}`, body);
        setUsers((prev) => prev.map((u) => (u.id === editItem.id ? data : u)));
      } else {
        const data = await api.post("/admin/users", body);
        setUsers((prev) => [data, ...prev]);
      }
      closeModal();
    } catch (err) {
      setModalError(err.message || "Save failed");
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setModalError("");
    const form = e.target;
    const body = { name: form.name.value, slug: form.slug.value };

    try {
      if (editItem) {
        const data = await api.put(`/admin/categories/${editItem.id}`, body);
        setCategories((prev) => prev.map((c) => (c.id === editItem.id ? data : c)));
      } else {
        const data = await api.post("/admin/categories", body);
        setCategories((prev) => [...prev, data]);
      }
      closeModal();
    } catch (err) {
      setModalError(err.message || "Save failed");
    }
  };

  const renderModal = () => {
    if (!showModal) return null;

    return (
      <div className="admin-overlay" onClick={closeModal}>
        <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
          {showModal === "game" && (
            <>
              <h3>{editItem ? "Edit Game" : "Add Game"}</h3>
              {modalError && <p className="auth-error">{modalError}</p>}
              <form onSubmit={handleSaveGame} className="admin-form">
                <input name="title" placeholder="Game Title" required defaultValue={editItem?.title || ""} />
                <div className="form-row">
                  <select name="category_id" required style={{ flex: 1 }} defaultValue={editItem?.category_id || ""}>
                    <option value="">Category</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <input name="price" type="number" placeholder="Price (Tshs)" required style={{ flex: 1 }} defaultValue={editItem?.price || ""} />
                </div>
                <textarea name="description" rows="3" placeholder="Game description" defaultValue={editItem?.description || ""} />
                <input name="trailer_url" placeholder="Trailer URL (YouTube link)" defaultValue={editItem?.trailer_url || ""} />
                <label className="admin-file-label">
                  Cover Image {editItem && "(leave empty to keep current)"}
                  <input type="file" accept="image/*" name="cover" />
                </label>
                <label className="admin-file-label">
                  Thumbnail {editItem && "(leave empty to keep current)"}
                  <input type="file" accept="image/*" name="thumbnail" />
                </label>
                <label className="admin-file-label">
                  <span>Screenshots (max 10 images)</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleScreenshots}
                    disabled={screenshots.length >= 10}
                  />
                  {screenshots.length > 0 && (
                    <div className="screenshot-previews">
                      {screenshots.map((file, i) => (
                        <div key={i} className="screenshot-preview-item">
                          <img src={URL.createObjectURL(file)} alt={`Screenshot ${i + 1}`} />
                          <button type="button" className="remove-screenshot-btn" onClick={() => removeScreenshot(i)}>
                            &times;
                          </button>
                          <span className="upload-filename">{file.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {screenshots.length > 0 && (
                    <p className="screenshot-count">{screenshots.length} / 10 images selected</p>
                  )}
                </label>
                <fieldset className="versions-fieldset">
                  <legend>{editItem ? "New Version" : "Initial Version"}</legend>
                  <div className="form-row">
                    <input name="version" placeholder="Version (e.g. 1.0.0)" required style={{ flex: 1 }} defaultValue="1.0.0" />
                    <input name="download_link" placeholder="Download Link (URL)" style={{ flex: 2 }} />
                  </div>
                  <textarea name="changelog" rows="2" placeholder="Changelog" />
                </fieldset>
                <div className="admin-form-actions">
                  {uploading && (
                    <div className="upload-progress">
                      <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                      <span className="upload-progress-text">{uploadProgress}%</span>
                    </div>
                  )}
                  <button type="submit" className="auth-submit" disabled={uploading}>
                    {uploading ? "Uploading..." : editItem ? "Update Game" : "Add Game"}
                  </button>
                  <button type="button" className="auth-submit" style={{ background: "var(--navy-border)" }} onClick={closeModal}>Cancel</button>
                </div>
              </form>
            </>
          )}

          {showModal === "user" && (
            <>
              <h3>{editItem ? "Edit User" : "Add User"}</h3>
              {modalError && <p className="auth-error">{modalError}</p>}
              <form onSubmit={handleSaveUser} className="admin-form">
                <input name="username" placeholder="Username" required defaultValue={editItem?.username || ""} />
                <input name="phone" placeholder="Phone" required defaultValue={editItem?.phone || ""} />
                <input name="password" type="password" placeholder={editItem ? "New password (leave empty to keep)" : "Password"} required={!editItem} />
                <select name="role" required defaultValue={editItem?.role || "user"}>
                  <option value="user">User</option>
                  <option value="developer">Developer</option>
                  <option value="admin">Admin</option>
                </select>
                <textarea name="bio" rows="2" placeholder="Bio (for developers)" defaultValue={editItem?.bio || ""} />
                <div className="admin-form-actions">
                  <button type="submit" className="auth-submit">{editItem ? "Update" : "Add"} User</button>
                  <button type="button" className="auth-submit" style={{ background: "var(--navy-border)" }} onClick={closeModal}>Cancel</button>
                </div>
              </form>
            </>
          )}

          {showModal === "category" && (
            <>
              <h3>{editItem ? "Edit Category" : "Add Category"}</h3>
              {modalError && <p className="auth-error">{modalError}</p>}
              <form onSubmit={handleSaveCategory} className="admin-form">
                <input name="name" placeholder="Category Name" required defaultValue={editItem?.name || ""} />
                <input name="slug" placeholder="Slug (e.g. action-rpg)" required defaultValue={editItem?.slug || ""} />
                <div className="admin-form-actions">
                  <button type="submit" className="auth-submit">{editItem ? "Update" : "Add"} Category</button>
                  <button type="button" className="auth-submit" style={{ background: "var(--navy-border)" }} onClick={closeModal}>Cancel</button>
                </div>
              </form>
            </>
          )}

          {showModal === "developer" && (
            <>
              <h3>{editItem ? "Edit Developer" : "Add Developer"}</h3>
              {modalError && <p className="auth-error">{modalError}</p>}
              <form onSubmit={handleSaveDeveloper} className="admin-form">
                <input name="username" placeholder="Developer Name" required defaultValue={editItem?.username || editItem?.name || ""} />
                <input name="email" type="email" placeholder="Email" required defaultValue={editItem?.email || ""} />
                <input name="phone" placeholder="Phone" required defaultValue={editItem?.phone || ""} />
                <input name="password" type="password" placeholder={editItem ? "New password (leave empty to keep)" : "Password"} required={!editItem} />
                <textarea name="bio" rows="3" placeholder="Bio" defaultValue={editItem?.bio || ""} />
                <div className="admin-form-actions">
                  <button type="submit" className="auth-submit">{editItem ? "Update" : "Add"} Developer</button>
                  <button type="button" className="auth-submit" style={{ background: "var(--navy-border)" }} onClick={closeModal}>Cancel</button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>
      <p className="admin-subtitle">Full control over the BongoGames platform</p>

      {overview && (
        <div className="admin-stats">
          <div className="stat-card">
            <span className="stat-value">{overview.total_games}</span>
            <span className="stat-label">Games</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{overview.total_users}</span>
            <span className="stat-label">Users</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{overview.total_developers}</span>
            <span className="stat-label">Developers</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{overview.total_sales}</span>
            <span className="stat-label">Sales</span>
          </div>
          <div className="stat-card">
            <span className="stat-value">{formatPrice(overview.total_revenue)}</span>
            <span className="stat-label">Revenue</span>
          </div>
        </div>
      )}

      <div className="admin-tabs">
        {["overview", "games", "categories", "users", "developers", "sales"].map((tab) => (
          <button
            key={tab}
            className={`admin-tab ${activeTab === tab ? "active" : ""}`}
            onClick={() => switchTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === "overview" && overview && (
        <div className="admin-section">
          <h3>Platform Overview</h3>
          <div className="overview-grid">
            <div className="overview-card">
              <span className="overview-icon overview-icon-game">G</span>
              <div>
                <span className="overview-value">{overview.total_games}</span>
                <span className="overview-label">Games</span>
              </div>
            </div>
            <div className="overview-card">
              <span className="overview-icon overview-icon-cat">C</span>
              <div>
                <span className="overview-value">{categories.length || 8}</span>
                <span className="overview-label">Categories</span>
              </div>
            </div>
            <div className="overview-card">
              <span className="overview-icon overview-icon-user">U</span>
              <div>
                <span className="overview-value">{overview.total_users}</span>
                <span className="overview-label">Users</span>
              </div>
            </div>
            <div className="overview-card">
              <span className="overview-icon overview-icon-dev">D</span>
              <div>
                <span className="overview-value">{overview.total_developers}</span>
                <span className="overview-label">Developers</span>
              </div>
            </div>
            <div className="overview-card">
              <span className="overview-icon overview-icon-sale">S</span>
              <div>
                <span className="overview-value">{overview.total_sales}</span>
                <span className="overview-label">Sales</span>
              </div>
            </div>
            <div className="overview-card">
              <span className="overview-icon overview-icon-rev">R</span>
              <div>
                <span className="overview-value">{formatPrice(overview.total_revenue)}</span>
                <span className="overview-label">Revenue</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "games" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <h3>Games ({tabLoading ? "..." : games.length})</h3>
            <button className="admin-add-btn" onClick={() => openModal("game")}>+ Add Game</button>
          </div>
          <div className="admin-table">
            <div className="admin-table-header">
              <span>Game</span><span>Category</span><span>Price</span><span>Status</span><span>Actions</span>
            </div>
            {tabLoading
              ? Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} columns={5} />)
              : games.map((game) => (
              <div key={game.id} className="admin-table-row">
                <span className="admin-game-title">{game.title}</span>
                <span>{game.category?.name || ""}</span>
                <span className="game-card-price">{formatPrice(game.price)}</span>
                <span>{game.status}</span>
                <span style={{ display: "flex", gap: 4 }}>
                  <button className="admin-action-btn" onClick={() => openViewGame(game)}>View</button>
                  <select value={game.status} onChange={(e) => handleUpdateStatus(game.id, e.target.value)}>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <button className="admin-action-btn" onClick={() => openModal("game", game)}>Edit</button>
                  <button className="admin-action-btn danger" onClick={() => handleDeleteGame(game.id)}>Delete</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "categories" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <h3>Categories ({tabLoading ? "..." : categories.length})</h3>
            <button className="admin-add-btn" onClick={() => openModal("category")}>+ Add Category</button>
          </div>
          <div className="admin-table">
            <div className="admin-table-header">
              <span>Name</span><span>Slug</span><span>Games</span><span>Actions</span>
            </div>
            {tabLoading
              ? Array.from({ length: 4 }).map((_, i) => <TableRowSkeleton key={i} columns={4} />)
              : categories.map((cat) => (
              <div key={cat.id} className="admin-table-row">
                <span>{cat.name}</span>
                <span>{cat.slug}</span>
                <span>{cat.games_count ?? 0}</span>
                <span style={{ display: "flex", gap: 4 }}>
                  <button className="admin-action-btn" onClick={() => openModal("category", cat)}>Edit</button>
                  <button className="admin-action-btn danger" onClick={() => handleDeleteCategory(cat.id)}>Delete</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "users" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <h3>Users ({tabLoading ? "..." : users.length})</h3>
            <button className="admin-add-btn" onClick={() => openModal("user")}>+ Add User</button>
          </div>
          <div className="admin-table">
            <div className="admin-table-header">
              <span>Username</span><span>Phone</span><span>Role</span><span>Actions</span>
            </div>
            {tabLoading
              ? Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} columns={4} />)
              : users.map((u) => (
              <div key={u.id} className="admin-table-row">
                <span>{u.username}</span>
                <span>{u.phone}</span>
                <span>{u.role}</span>
                <span style={{ display: "flex", gap: 4 }}>
                  <button className="admin-action-btn" onClick={() => openModal("user", u)}>Edit</button>
                  <button className="admin-action-btn danger" onClick={() => handleDeleteUser(u.id)}>Delete</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "developers" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <h3>Developers ({tabLoading ? "..." : developers.length})</h3>
            <button className="admin-add-btn" onClick={() => openModal("developer")}>+ Add Developer</button>
          </div>
          <div className="admin-table">
            <div className="admin-table-header">
              <span>Name</span><span>Email</span><span>Bio</span><span>Actions</span>
            </div>
            {tabLoading
              ? Array.from({ length: 4 }).map((_, i) => <TableRowSkeleton key={i} columns={4} />)
              : developers.map((d) => (
              <div key={d.id} className="admin-table-row">
                <span>{d.username || d.name}</span>
                <span>{d.email}</span>
                <span className="admin-bio">{d.bio}</span>
                <span>
                  <button className="admin-action-btn danger" onClick={() => handleDeleteDev(d.id)}>Delete</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "sales" && (
        <div className="admin-section">
          <h3>Sales History ({tabLoading ? "..." : sales.length})</h3>
          {tabLoading ? (
            <div className="admin-table">
              <div className="admin-table-header">
                <span>Game</span><span>User</span><span>Price</span><span>Date</span>
              </div>
              {Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} columns={4} />)}
            </div>
          ) : sales.length === 0 ? (
            <p style={{ color: "var(--text-muted)" }}>No sales yet.</p>
          ) : (
            <div className="admin-table">
              <div className="admin-table-header">
                <span>Game</span><span>User</span><span>Price</span><span>Date</span>
              </div>
              {sales.map((s) => (
                <div key={s.id} className="admin-table-row">
                  <span>{s.game?.title || "Unknown"}</span>
                  <span>{s.user?.username || "Unknown"}</span>
                  <span className="game-card-price">{formatPrice(s.price_paid)}</span>
                  <span>{new Date(s.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {viewGame && (
        <div className="admin-overlay" onClick={closeViewGame}>
          <div className="admin-modal admin-view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="view-game-header">
              {viewGame.thumbnail && <img src={viewGame.thumbnail} alt={viewGame.title} className="view-game-thumb" />}
              <div>
                <h3>{viewGame.title}</h3>
                <span className="view-game-category">{viewGame.category?.name || "Uncategorized"}</span>
              </div>
            </div>
            <div className="view-game-body">
              <div className="view-game-field">
                <span className="view-game-label">Price</span>
                <span className="view-game-price">{formatPrice(viewGame.price)}</span>
              </div>
              <div className="view-game-field">
                <span className="view-game-label">Status</span>
                <span className={`view-game-status status-${viewGame.status}`}>{viewGame.status}</span>
              </div>
              <div className="view-game-field">
                <span className="view-game-label">Description</span>
                <p className="view-game-desc">{viewGame.description || "No description"}</p>
              </div>
              {viewGame.trailer_url && (
                <div className="view-game-field">
                  <span className="view-game-label">Trailer</span>
                  <a href={viewGame.trailer_url} target="_blank" rel="noopener noreferrer" className="view-game-link">{viewGame.trailer_url}</a>
                </div>
              )}
              {viewGame.cover && (
                <div className="view-game-field">
                  <span className="view-game-label">Cover Image</span>
                  <img src={viewGame.cover} alt={`${viewGame.title} cover`} className="view-game-cover" />
                </div>
              )}
              {viewGame.versions && viewGame.versions.length > 0 && (
                <div className="view-game-field">
                  <span className="view-game-label">Versions</span>
                  <div className="view-game-versions">
                    {viewGame.versions.map((v, i) => (
                      <div key={i} className="view-game-version">
                        <span className="version-tag">{v.version}</span>
                        {v.changelog && <span className="version-changelog">{v.changelog}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="admin-form-actions">
              <button className="auth-submit" onClick={closeViewGame}>Close</button>
            </div>
          </div>
        </div>
      )}
      {renderModal()}
    </div>
  );
}

export default AdminDashboard;
